import { podPhaseView } from "./pod-phase.js";
import { podProblemEnum } from "./app-pods.js";
import { z } from "zod";
//#region src/fleet-pod-problems.ts
/**
* The fleet pod problem read: `GET /v1/admin/data-planes/fleet/pod-problems` (plan
* 2026-10-21-operator-tui, gap G2 and decision D7, gate `cp:dp:read`). Stage S3 (WS-10) will add
* the route to site-admin-api; no controller serves it yet, so this schema is the contract that
* stage builds to.
*
* One call will answer "which pods crash, restart or wait, on every data plane". The route will read
* the pod list of each active data plane and keep the pods with a problem or a restart. The problem
* set is `POD_PROBLEMS` (`podProblemEnum`), derived by the same rule as the app and CP pod reads.
*
* A data plane the CP could not read gets an `unread` entry with a reason. It never gets an empty
* pod list, because an empty list means "read, and nothing wrong" (defaults row F8).
*/
/** The most problem pods the route returns for one data plane. `truncated` says when it bound. */
const FLEET_POD_PROBLEMS_MAX_PODS_PER_DP = 200;
/** The longest `reason` of an unread data plane. */
const FLEET_POD_PROBLEMS_REASON_MAX_LENGTH = 200;
/**
* One pod with a problem.
*
* - `phase`: the k8s phase; an unknown phase text reads as `Unknown`.
* - `restartCount`: the sum over the pod's containers.
* - `problems`: a set in `POD_PROBLEMS` order, de-duplicated across containers.
*
* A pod is listed only when `problems` is not empty or `restartCount` is above 0; a parse refuses
* a pod with neither.
*/
const fleetProblemPodSchema = z.object({
	name: z.string().min(1).max(253),
	namespace: z.string().min(1).max(63),
	phase: podPhaseView,
	restartCount: z.number().int().nonnegative(),
	problems: z.array(podProblemEnum)
}).strict().refine((pod) => pod.problems.length > 0 || pod.restartCount > 0, { message: "a listed pod has a problem or a restart" });
/**
* One data plane the CP read. `pods` is `[]` when no pod has a problem or a restart. `truncated`
* is true when more than {@link FLEET_POD_PROBLEMS_MAX_PODS_PER_DP} pods qualified and the rest
* were dropped.
*/
const fleetPodProblemsReadDpSchema = z.object({
	status: z.literal("read"),
	dpId: z.uuid(),
	name: z.string().min(1),
	pods: z.array(fleetProblemPodSchema).max(200),
	truncated: z.boolean()
}).strict();
/**
* One data plane the CP could not read. `reason` is CP-written text for an operator, at most
* 200 characters. It never carries the upstream body, which can hold internal host names.
*/
const fleetPodProblemsUnreadDpSchema = z.object({
	status: z.literal("unread"),
	dpId: z.uuid(),
	name: z.string().min(1),
	reason: z.string().min(1).max(200)
}).strict();
/** One data plane entry: read with its pods, or unread with a reason. */
const fleetPodProblemsDpSchema = z.discriminatedUnion("status", [fleetPodProblemsReadDpSchema, fleetPodProblemsUnreadDpSchema]);
/**
* `GET /v1/admin/data-planes/fleet/pod-problems`: one entry per active data plane, ordered by
* name. `partial` is true when at least one entry is `unread`. `fetchedAt` is the CP's ISO-8601
* read time.
*/
const fleetPodProblemsResponseSchema = z.object({
	dataPlanes: z.array(fleetPodProblemsDpSchema),
	partial: z.boolean(),
	fetchedAt: z.iso.datetime()
}).strict().refine((body) => body.partial === body.dataPlanes.some((dp) => dp.status === "unread"), {
	message: "partial is true exactly when some data plane is unread",
	path: ["partial"]
});
//#endregion
export { FLEET_POD_PROBLEMS_MAX_PODS_PER_DP, FLEET_POD_PROBLEMS_REASON_MAX_LENGTH, fleetPodProblemsDpSchema, fleetPodProblemsReadDpSchema, fleetPodProblemsResponseSchema, fleetPodProblemsUnreadDpSchema, fleetProblemPodSchema };

//# sourceMappingURL=fleet-pod-problems.js.map